const REFCODE_STORAGE_KEY = 'refcode';
const UNIQUE_WALLETS_KEY = 'unique_wallets';

/**
 * Get refcode from URL (using same method as swapUtils.ts)
 */
export const getRefCodeFromURL = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('refcode') || undefined;
};

/**
 * Get refcode from localStorage
 */
export const getRefCodeFromStorage = (): string | null => {
  try {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(REFCODE_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

/**
 * Save refcode to localStorage
 */
export const saveRefCodeToStorage = (refCode: string): void => {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(REFCODE_STORAGE_KEY, refCode);
  } catch (error) {
    // Error saving refcode
  }
};

/**
 * Get refcode: first check URL, if not found check localStorage
 * If not found in both, set default refcode from JSON file
 */
export const getRefCode = (): string | null => {
  // First check URL
  const urlRefCode = getRefCodeFromURL();
  if (urlRefCode) {
    // Save to localStorage if found in URL
    saveRefCodeToStorage(urlRefCode);
    return urlRefCode;
  }

  // If not in URL, check localStorage
  const storedRefCode = getRefCodeFromStorage();
  if (storedRefCode) {
    return storedRefCode;
  }

  // If not found in both, set default refcode from JSON
  try {
    const refcodesData = require('../refcodes.json');
    const defaultRefCode = refcodesData.default?.refCode;
    if (defaultRefCode) {
      saveRefCodeToStorage(defaultRefCode);
      return defaultRefCode;
    }
  } catch (error) {
    // Error loading refcodes.json
  }

  return null;
};

/**
 * Get list of unique wallet addresses from localStorage
 */
export const getUniqueWallets = (): string[] => {
  try {
    if (typeof window === 'undefined') return [];
    const stored = localStorage.getItem(UNIQUE_WALLETS_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as string[];
  } catch (error) {
    return [];
  }
};

/**
 * Add wallet address to unique wallets list (if not already exists)
 * Returns true if wallet is new, false if already exists
 */
export const addUniqueWallet = (walletAddress: string): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    
    const wallets = getUniqueWallets();
    const normalizedAddress = walletAddress.toLowerCase();
    
    // Check if wallet already exists
    if (wallets.includes(normalizedAddress)) {
      return false;
    }
    
    // Add new wallet
    wallets.push(normalizedAddress);
    localStorage.setItem(UNIQUE_WALLETS_KEY, JSON.stringify(wallets));
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Clear all localStorage data
 * This is called when user disconnects wallet
 * Note: refcode and unique_wallets are preserved and not cleared
 */
export const clearAllLocalStorage = (): void => {
  try {
    // Save refcode and unique wallets before clearing
    const savedRefCode = getRefCodeFromStorage();
    const savedUniqueWallets = localStorage.getItem(UNIQUE_WALLETS_KEY);
    
    // Get all keys from localStorage
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }

    // Remove all keys
    keys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Restore refcode and unique wallets after clearing
    if (savedRefCode) {
      saveRefCodeToStorage(savedRefCode);
    }
    if (savedUniqueWallets) {
      localStorage.setItem(UNIQUE_WALLETS_KEY, savedUniqueWallets);
    }
  } catch (error) {
    // Error clearing localStorage
  }
};

